import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { cellStringComparator, onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { WMS1060_R01Dto, defaultWMS1060_R01Dto } from './Dto_WMS1060_R01';
import { useWMS1060_R01Store } from './Store_WMS1060_R01';

const COLUMN_STATE_KEY = 'WMS1060_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1060_R01Dto> | null>;
}

const formatNumberWithCommas = (value: number | null | undefined): string => {
	if (value === null || value === undefined) return '0';
	return value.toLocaleString('ko-KR');
};

export const Table_WMS1060_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		WMS1060_R01List,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useWMS1060_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS1060_R01List,
		defaultGridData: defaultWMS1060_R01Dto,
		selectedRow: undefined,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		inoutType: '구분',
		transportDt: '일자',
		inoutClassNm: '수불유형',
		logicCenterNm: '물류센터',
		warehouseNm: '창고명',
		customCd: '거래처',
		customNm: '상호',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		itemStatusNm: '등급',
		unit: '단위',
		qty: '수불량',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'inoutType')(params), []);

	const baseColumnDefs: ColDef<WMS1060_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: headerNames['inoutType'], colId: 'inoutType', field: 'inoutType', width: 60,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'inoutType'),
			valueFormatter: (params: any) => {
				if (params.node.isRowPinned()) return params.value;
				return params.value === 'OUT' ? '출고' : '입고';
			},
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['transportDt'], colId: 'transportDt', field: 'transportDt', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'transportDt'),
			valueFormatter: (params: any) =>
				params.value != null && params.value !== '' ? formatDate(params.value) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: headerNames['inoutClassNm'], colId: 'inoutClassNm', field: 'inoutClassNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'inoutClassNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['logicCenterNm'], colId: 'logicCenterNm', field: 'logicCenterNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'logicCenterNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['warehouseNm'], colId: 'warehouseNm', field: 'warehouseNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'warehouseNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['customCd'], colId: 'customCd', field: 'customCd', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['customNm'], colId: 'customNm', field: 'customNm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['itemCd'], colId: 'itemCd', field: 'itemCd', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['itemNm'], colId: 'itemNm', field: 'itemNm', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['itemSpec'], colId: 'itemSpec', field: 'itemSpec', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['locationNm'], colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['itemStatusNm'], colId: 'itemStatusNm', field: 'itemStatusNm', width: 60,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemStatusNm'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['unit'], colId: 'unit', field: 'unit', width: 60,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'unit'),
			comparator: cellStringComparator,
		},
		{
			headerName: headerNames['qty'], colId: 'qty', field: 'qty', width: 100,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'qty'),
			valueFormatter: (params) => formatNumberWithCommas(params.value),
			comparator: (valueA: any, valueB: any) => (valueA ?? 0) - (valueB ?? 0),
		},
		{
			headerName: headerNames['remark'], colId: 'remark', field: 'remark', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			comparator: cellStringComparator,
		},
	], [headerNames]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1060_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS1060_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		inoutType: `${WMS1060_R01List.length.toLocaleString()}건`,
		qty: WMS1060_R01List.reduce((sum, item) => (sum + (item.qty || 0)), 0),
		editable: false,
	}]), [WMS1060_R01List]);

	return (
		<GridContextWrapper<WMS1060_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={() => {}}
			defaultRowData={defaultWMS1060_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<WMS1060_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<WMS1060_R01Dto>({
					idKey: 'valueIndex',
					selectedList: [],
					deletedList: [],
				})}
				onRowClicked={undefined}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default Table_WMS1060_R01;
